import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";
import axios, { CancelToken } from "axios";
import ReactDOM from "react-dom";
import moment from "moment";
import { isEqualWith, isNull } from "lodash";

import { StatusCode } from "api/protocols";

import { GetInvoiceTransaction, GetInvoiceTransactionReceipt } from "api/rpc/2024-04/facilityAdmin/order/invoice";

import {
  DeleteTournamentLogo,
  UploadTournamentLogo,
  GetTournamentSponsorship,
  PostTournamentSponsorship,
  DeleteTournamentSponsorship,
  GetTournament,
  PutTournament,
  DeleteTournament,
} from "api/rpc/2024-04/facilityAdmin/tournament/tournament";

import { showError, showSuccess } from "redux/actions/ui";
import { formatDate, handleChangeEventInput } from "helpers/Helpers";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import useModal from "hooks/modals/useModal";

import Page from "components/page/Page";
import Card from "components/card/Card";
import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import TextField from "components/form/textField/TextField";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import TimePick from "components/timePick/TimePick";
import Popup from "components/popup/Popup";
import Portal from "elements/Portal";
import TextEditor from "components/textEditor/textEditor";
import DataTable from "../../../../../../components/dataTable/DataTable";
import { LocaleCurrency } from "helpers/Locale";
import { ButtonNew as Button } from "components/buttonNew";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Checkbox from "components/form/checkbox/Checkbox";
import SponsorList, { ISponsorshipImage } from "components/sponsorList/SponsorList";
import { IInvoiceTransaction } from "redux/reducers/models/order";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import { Badge } from "components/badge/Badge";
import CustomerImage from "elements/customer/CustomerImage";
import { INotificationBarProps } from "components/notificationBar/NotificationBar";

import "./tournamentSettingsLanding.scss";

interface ITournamentState {
  id: number;
  name: string;
  start_time: string;
  date: Date;
  end_date: Date | null;
  description: string;
  handle: string;
  tee_sheet_note: string;
  registration_link: string;
  logo_source: string;
  display_online: boolean;
  display_registration_online: boolean;
  deposits: Array<IInvoiceTransaction>;
}

type EditableSettingsSnapshot = {
  name: string;
  start_time: string;
  date: Date;
  end_date: Date | null;
  show_end_date: boolean;
  description: string;
  tee_sheet_note: string;
  display_online: boolean;
  display_registration_online: boolean;
};

interface ITournamentSettingsProps {
  /** When true, render tab content only (no Page chrome). */
  embedded?: boolean;
  onNotificationBarPropsChange?: (props: INotificationBarProps | null) => void;
}

function cloneEditableSnapshot(source: ITournamentState, showEndDate: boolean): EditableSettingsSnapshot {
  return {
    name: source.name ?? "",
    start_time: source.start_time ?? "",
    date: source.date ? new Date(source.date.getTime()) : null,
    end_date: source.end_date ? new Date(source.end_date.getTime()) : null,
    show_end_date: showEndDate,
    description: source.description ?? "",
    tee_sheet_note: source.tee_sheet_note ?? "",
    display_online: Boolean(source.display_online),
    display_registration_online: Boolean(source.display_registration_online),
  };
}

function parseCalendarDate(value?: string | null): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? "");
  if (!match) {
    return null;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }

  return date;
}

function datesEqual(a: Date | null, b: Date | null): boolean {
  if (!a && !b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  return (
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
  );
}

export default function TournamentSettings(props: ITournamentSettingsProps) {
  const { embedded = false, onNotificationBarPropsChange } = props;
  const history = useHistory();
  const { t } = useTranslation();
  const { tournamentId } = useParams<{ tournamentId: string }>();

  const dispatch = useAppDispatch();

  const [state, setState] = useState<ITournamentState>({
    id: Number(tournamentId),
    name: "",
    start_time: "",
    date: new Date(),
    end_date: null,
    description: "",
    handle: "",
    tee_sheet_note: "",
    registration_link: "",
    logo_source: "",
    display_online: false,
    display_registration_online: false,
    deposits: null,
  });
  const [stateBeforeChanges, setStateBeforeChanges] = useState<EditableSettingsSnapshot>(undefined);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [endDate, setEndDate] = useState(false);

  const [deleteTournamentPopup, setDeleteTournamentPopup] = useState(false);
  const { facilityStore } = useAppSelector(store => store);

  const { state: removeSponsorshipPopup, updateModal, closeModal } = useModal({ sponsorId: null as number });

  const [sponsorships, setSponsorships] = useState<ISponsorshipImage[]>(undefined);

  useEffect(() => {
    const source = axios.CancelToken.source();

    if (facilityStore.facility) {
      void loadTournament(tournamentId, source.token);
      void loadSponsorships(tournamentId, source.token);
    }

    return () => source.cancel("Cancelled");
  }, [tournamentId, facilityStore]);

  async function loadTournament(id: number | string, token?: CancelToken) {
    const tournamentRes = await GetTournament({ id: id }, true, token);

    if (tournamentRes.status !== StatusCode.OK) {
      if (tournamentRes.message === "Cancelled") {
        return;
      }
      dispatch(
        showError(
          typeof tournamentRes.data === "string"
            ? tournamentRes.data
            : t("secure.facility.tournament.settings.tournament_settings.001"),
        ),
      );
      return;
    }

    const tournament = tournamentRes.data[0];

    const depositRes = await GetInvoiceTransaction(
      { tournament_id: Number(tournamentId) },
      true,
    );

    if (depositRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading tournament deposits")));
      return;
    }

    const parsedStartDate = parseCalendarDate(tournament?.date) ?? new Date();
    const parsedEndDate = parseCalendarDate(tournament?.end_date);
    const hasEndDate = Boolean(parsedEndDate);

    const nextState: ITournamentState = {
      ...state,
      id: tournament.id,
      name: tournament.name,
      // API returns HH:mm:ss; TimePick expects HH:mm
      start_time: tournament.start_time?.substring(0, 5) ?? "",
      date: parsedStartDate,
      end_date: parsedEndDate,
      description: tournament.description ?? "",
      handle: tournament.handle,
      tee_sheet_note: tournament.tee_sheet_note ?? "",
      registration_link: window.origin + `/tournament/${facilityStore?.facility?.short_name}/${tournament.handle}/`,
      logo_source: tournament.logo_source,
      display_online: Boolean(tournament.display_online),
      display_registration_online: Boolean(tournament.display_registration_online),
      deposits: depositRes.data,
    };

    // Batch so End DatePicker never mounts with a null date (React 17 async updates).
    ReactDOM.unstable_batchedUpdates(() => {
      setEndDate(hasEndDate);
      setState(nextState);
      setStateBeforeChanges(cloneEditableSnapshot(nextState, hasEndDate));
      setSettingsLoaded(true);
    });
  }

  async function saveTournament() {
    if (!state.date) {
      return;
    }

    const year = state.date.toLocaleString("en-CA", { year: "numeric" });
    const month = state.date.toLocaleString("en-CA", { month: "2-digit" });
    const day = state.date.toLocaleString("en-CA", { day: "2-digit" });
    const dateString = `${year}-${month}-${day}`;

    const tournamentRes = await PutTournament(
      {
        id: state.id,
        name: state.name,
        start_time: state.start_time ?? null,
        date: dateString,
        end_date: endDate && state.end_date ? formatDate(state.end_date) : null,
        description: state.description,
        display_online: state.display_online,
        display_registration_online: state.display_registration_online,
        tee_sheet_note: state.tee_sheet_note,
      },
      true,
    );

    if (tournamentRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.tournament.settings.tournament_settings.002")));
      return;
    }

    dispatch(showSuccess(t("secure.facility.tournament.settings.tournament_settings.003")));
    setStateBeforeChanges(cloneEditableSnapshot(state, endDate));
  }

  function unsavedChangesExist() {
    if (!settingsLoaded || !stateBeforeChanges) {
      return false;
    }

    return (
      !isEqualWith(
        {
          name: stateBeforeChanges.name,
          start_time: stateBeforeChanges.start_time,
          description: stateBeforeChanges.description,
          tee_sheet_note: stateBeforeChanges.tee_sheet_note,
          display_online: stateBeforeChanges.display_online,
          display_registration_online: stateBeforeChanges.display_registration_online,
          show_end_date: stateBeforeChanges.show_end_date,
        },
        {
          name: state.name ?? "",
          start_time: state.start_time ?? "",
          description: state.description ?? "",
          tee_sheet_note: state.tee_sheet_note ?? "",
          display_online: Boolean(state.display_online),
          display_registration_online: Boolean(state.display_registration_online),
          show_end_date: endDate,
        },
        (originalValue, newValue) => {
          if ((isNull(originalValue) || originalValue === "") && (isNull(newValue) || newValue === "")) {
            return true;
          }
        },
      ) ||
      !datesEqual(stateBeforeChanges.date, state.date) ||
      !datesEqual(stateBeforeChanges.end_date, endDate ? state.end_date : null)
    );
  }

  function cancelUnsavedChanges() {
    if (!stateBeforeChanges) {
      return;
    }

    setEndDate(stateBeforeChanges.show_end_date);
    setState(prev => ({
      ...prev,
      name: stateBeforeChanges.name,
      start_time: stateBeforeChanges.start_time,
      date: stateBeforeChanges.date ? new Date(stateBeforeChanges.date.getTime()) : prev.date,
      end_date: stateBeforeChanges.end_date ? new Date(stateBeforeChanges.end_date.getTime()) : null,
      description: stateBeforeChanges.description,
      tee_sheet_note: stateBeforeChanges.tee_sheet_note,
      display_online: stateBeforeChanges.display_online,
      display_registration_online: stateBeforeChanges.display_registration_online,
    }));
  }

  useEffect(() => {
    if (!onNotificationBarPropsChange) {
      return;
    }

    onNotificationBarPropsChange({
      isVisible: unsavedChangesExist(),
      onAction: () => void saveTournament(),
      onCancel: cancelUnsavedChanges,
    });
  }, [state, stateBeforeChanges, settingsLoaded, endDate, onNotificationBarPropsChange]);

  async function removeTournament() {
    const res = await DeleteTournament({ id: tournamentId }, true);

    if (res.status !== StatusCode.OK) {
      dispatch(
        showError(
          typeof res.data === "string" ? res.data : t("secure.facility.tournament.settings.tournament_settings.004"),
        ),
      );
      dispatch(
        showError(
          typeof res.data === "string" ? res.data : t("secure.facility.tournament.settings.tournament_settings.022"),
        ),
      );
      setDeleteTournamentPopup(false);
      return;
    }

    setDeleteTournamentPopup(false);
    history.push("/admin/events-tournament");
  }

  function handleDateChange(updatedDate: Date) {
    setState(prevState => ({ ...prevState, date: updatedDate }));
  }

  async function loadSponsorships(tournamentId: string, token?: CancelToken) {
    if (sponsorships !== undefined) {
      setSponsorships(undefined);
    }

    const res = await GetTournamentSponsorship({ tournament_id: tournamentId }, false, token);

    if (res.status !== StatusCode.OK) {
      if (res.message === "Cancelled") {
        return;
      }
      dispatch(
        showError(
          typeof res.data === "string" ? res.data : t("secure.facility.tournament.settings.tournament_settings.023"),
        ),
      );
      setSponsorships([]);
      return;
    }

    setSponsorships(res.data);
  }

  async function uploadSponsorship(image: File) {
    const formData = new FormData();
    formData.append("image", image);
    formData.append("tournament_id", String(tournamentId));

    const res = await PostTournamentSponsorship(formData, true);
    if (res.status !== StatusCode.OK) {
      dispatch(showError(typeof res.data === "string" ? res.data : t("Error uploading sponsor")));
      return;
    }

    void loadSponsorships(String(tournamentId));
  }

  async function removeSponsorship() {
    const res = await DeleteTournamentSponsorship({ id: removeSponsorshipPopup.sponsorId }, true);

    if (res.status !== StatusCode.OK) {
      dispatch(
        showError(
          typeof res.data === "string" ? res.data : t("secure.facility.tournament.settings.tournament_settings.025"),
        ),
      );
      return;
    }

    const updatedSponsors = [...sponsorships];
    const removedSponsor = updatedSponsors.indexOf(
      sponsorships.find(value => value.id === removeSponsorshipPopup.sponsorId),
    );
    updatedSponsors.splice(removedSponsor, 1);

    setSponsorships(updatedSponsors);
    closeModal();
  }

  async function getDepositReceipt(transactionId: number) {
    const res = await GetInvoiceTransactionReceipt({ transaction_id: transactionId }, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error printing deposit receipt")));
      return;
    }

    window.open().document.write(String(res.data));
  }

  function handleTournamentDescription(description: string) {
    setState(prevState => ({ ...prevState, description }));
  }

  async function saveTournamentLogo(imageFile: File) {
    const formData = new FormData();

    formData.append("image", imageFile);
    formData.append("tournament_id", state.id.toString());

    const imageRes = await UploadTournamentLogo(formData, true);
    if (imageRes.status !== StatusCode.OK) {
      return;
    }

    setState(prevState => ({ ...prevState, logo_source: imageRes.data.logo_source }));
  }

  async function deleteTournamentLogo() {
    const deleteRes = await DeleteTournamentLogo({ tournament_id: tournamentId }, true);

    if (deleteRes.status !== StatusCode.OK) {
      return;
    }

    setState(prevState => ({ ...prevState, logo_source: deleteRes.data.logo_source }));
  }

  function handleNoteInputChange(event: any) {
    const { id, value } = event.target;
    setState(prev => ({
      ...prev,
      [id]: value,
    }));
  }

  const statusBadge = (deposit: IInvoiceTransaction) => {
    if (deposit.refund_transaction !== null) {
      return <Badge type="warning">{t("Refunded")}</Badge>;
    }

    if (deposit.invoice_id === null) {
      return <Badge type="success">{t("Available")}</Badge>;
    }

    return <Badge type="gray">{t("Applied")}</Badge>;
  };

  const content = (
    <>
      <Form>
        <Card title={t("secure.facility.tournament.settings.tournament_settings.010")}>
          <Card.Section>
            <FormLayout>
              <FormLayout.Group>
                <Input
                  value={state.name}
                  label={t("secure.facility.tournament.settings.tournament_settings.011")}
                  id="name"
                  onChange={e => handleChangeEventInput(e, state, setState)}
                  placeholder={t("secure.facility.tournament.settings.tournament_settings.012")}
                />
              </FormLayout.Group>
            </FormLayout>
            <FormLayout>
              <FormLayout.Group>
                <TimePick
                  label={t("secure.facility.tournament.settings.tournament_settings.013")}
                  value={state.start_time}
                  onChange={(timeString: string) => setState(prev => ({ ...prev, start_time: timeString }))}
                  size="large"
                  status={state?.start_time === undefined ? "error" : undefined}
                />
              </FormLayout.Group>
              <FormLayout.Group>
                <DatePickerInput
                  label={t("Start Date")}
                  months={1}
                  position="left"
                  startingDate={state?.date}
                  setStartingDate={handleDateChange}
                />
                {!endDate || !state.end_date ? (
                  <Button
                    size="medium"
                    type="secondary"
                    onClick={() => {
                      const nextEndDate = new Date();
                      setState(prev => ({ ...prev, end_date: nextEndDate }));
                      setEndDate(true);
                    }}
                  >
                    {t("Set end date")}
                  </Button>
                ) : (
                  <DatePickerInput
                    label={t("End Date")}
                    months={1}
                    position="left"
                    startingDate={state.end_date}
                    setStartingDate={date => setState(prev => ({ ...prev, end_date: date as Date }))}
                  />
                )}
              </FormLayout.Group>
            </FormLayout>

            <FormLayout>
              <FormLayout.Group>
                <Checkbox
                  id="display_online"
                  size="medium"
                  value={state.display_online}
                  checked={state.display_online}
                  onChange={() => setState(prevState => ({ ...prevState, display_online: !state.display_online }))}
                  label={t("secure.facility.tournament.settings.tournament_settings.026")}
                  supportText={t("Would you like to publicly display this tournament in the client portal list and calendar?")}
                />
                <Checkbox
                  id="display_registration_online"
                  size="medium"
                  value={state.display_registration_online}
                  checked={state.display_registration_online}
                  onChange={() =>
                    setState(prevState => ({
                      ...prevState,
                      display_registration_online: !state.display_registration_online,
                    }))
                  }
                  label={t("Display Registration Online")}
                  supportText={t("Would you like to show online registration for this tournament?")}
                />
              </FormLayout.Group>
            </FormLayout>
          </Card.Section>
          <Card.Section title={t("Logo")}>
            <div className="stats_bar-picture">
              <CustomerImage
                imageSource={state.logo_source}
                saveProfileImage={imageFile => saveTournamentLogo(imageFile)}
                deleteProfileImage={() => deleteTournamentLogo()}
              />
            </div>
          </Card.Section>
          <Card.Section>
            <FormLayout>
              <FormLayout.Group>
                <TextEditor
                  markdownText={state.description}
                  markdownTextOnChange={handleTournamentDescription}
                  label={t("secure.facility.tournament.settings.tournament_settings.015")}
                />
              </FormLayout.Group>
            </FormLayout>
          </Card.Section>
        </Card>

        <div className="mt-4">
          <Card title={t("Sponsors")}>
            <Card.Section>
              <SponsorList
                sponsors={sponsorships}
                uploadLabel={t("Upload sponsor")}
                onNew={image => void uploadSponsorship(image)}
                onRemove={sponsorId => updateModal({ isOpen: true, sponsorId })}
              />
            </Card.Section>
          </Card>
        </div>

        <Card title={t("secure.facility.tournament.settings.tournament_settings.016")}>
          <Card.Section>
            <TextField
              value={state.tee_sheet_note}
              label={t("secure.facility.tournament.settings.tournament_settings.017")}
              id="tee_sheet_note"
              onChange={handleNoteInputChange}
              maxLength={150}
              showCharacterCount
            />
          </Card.Section>
        </Card>
      </Form>

      <div className="mt-4">
        {state?.deposits?.length >= 1 && (
          <Card title={t("secure.facility.hospitality.hospitality_event.008")}>
            <Card.Section>
              <DataTable
                columns={[
                  { label: t("secure.facility.hospitality.hospitality_event.009"), width: "25%" },
                  { label: t("secure.facility.hospitality.hospitality_event.010"), width: "20%" },
                  { label: t("Status"), width: "10%" },
                  { label: t("secure.facility.hospitality.hospitality_event.011"), width: "40%" },
                  { label: "", width: "5%" },
                ]}
                loading={state.deposits === undefined}
              >
                {state.deposits
                  ?.filter(deposit => deposit.kind !== "refund")
                  .map((deposit, index) => (
                    <tr key={index}>
                      <td>{moment(deposit.processed_at_local).format("YYYY-MM-DD")}</td>
                      <td>
                        <LocaleCurrency currency={deposit.currency} amount={deposit.amount} />
                      </td>
                      <td>{statusBadge(deposit)}</td>
                      <td>{deposit.note}</td>
                      <td>
                        <NavigationDropdownNew
                          showPlainTextLabel
                          rightAlign
                          label={<FontAwesomeIcon icon={["far", "chevron-down"]} />}
                          sections={[
                            [
                              {
                                icon: "print",
                                label: t("Print Receipt"),
                                onClick: () => getDepositReceipt(deposit.id),
                              },
                            ],
                          ]}
                        />
                      </td>
                    </tr>
                  ))}
              </DataTable>
            </Card.Section>
          </Card>
        )}
      </div>

      <section className="tournament_settings-danger-zone">
        <h2 className="tournament_settings-danger-zone-heading">{t("Danger Zone")}</h2>
        <div className="tournament_settings-danger-zone-box">
          <div className="tournament_settings-danger-zone-row">
            <div className="tournament_settings-danger-zone-copy">
              <h3>{t("Delete this tournament")}</h3>
              <p>
                {t("Once you delete a tournament, there is no going back. Please be certain.")}
              </p>
            </div>
            <Button type="error-outline" onClick={() => setDeleteTournamentPopup(true)}>
              {t("Delete this tournament")}
            </Button>
          </div>
        </div>
      </section>

      <Portal isMounted={removeSponsorshipPopup.isOpen}>
        <Popup
          open={true}
          type={"warning"}
          title={t("secure.facility.tournament.settings.tournament_settings.028")}
          description={t("secure.facility.tournament.settings.tournament_settings.029")}
          onCancel={() => closeModal()}
          onOk={() => removeSponsorship()}
          okText={t("secure.facility.tournament.settings.tournament_settings.030")}
        />
      </Portal>

      <Portal isMounted={deleteTournamentPopup}>
        <Popup
          open={true}
          type={"warning"}
          title={t("secure.facility.tournament.settings.tournament_settings.019")}
          description={t("secure.facility.tournament.settings.tournament_settings.020")}
          onCancel={() => setDeleteTournamentPopup(false)}
          onOk={() => removeTournament()}
          okText={t("secure.facility.tournament.settings.tournament_settings.021")}
        />
      </Portal>
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <Page
      title={t("secure.facility.tournament.settings.tournament_settings.009")}
      narrow
      notificationBarProps={{
        isVisible: unsavedChangesExist(),
        onAction: () => void saveTournament(),
        onCancel: cancelUnsavedChanges,
      }}
    >
      {content}
    </Page>
  );
}
